import React, { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { mockDetailedData } from '../../data/mockAnalyticsData';
import KeywordCloud from './KeywordCloud';
import LoadingSkeleton from '../ui/LoadingSkeleton';
import { supabaseClient } from '../../utils/supabaseClient';
import { RefreshCw } from 'lucide-react';

interface DetailedAnalysisProps {
  isLoading: boolean;
  shopname: string;
  dateRange: { start: Date; end: Date };
}

interface RatingDistribution {
  stars: number;
  value: number;
}

interface SentimentAnalysis {
  category: string;
  positive: number;
  neutral: number;
  negative: number;
}

const DetailedAnalysis: React.FC<DetailedAnalysisProps & { periodLabel: string }> = ({ isLoading, shopname, dateRange, periodLabel }) => {
  const [activeTab, setActiveTab] = useState<'distribution' | 'keywords' | 'sentiment'>('distribution');
  const [ratingDistribution, setRatingDistribution] = useState<{ rating: number, count: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const { sentimentBreakdown } = mockDetailedData;
  const keywordCloudRef = useRef<{ fetchKeywordData: () => void }>(null);
  const [sentimentData, setSentimentData] = useState<{ positive: number; neutral: number; negative: number }>({ positive: 0, neutral: 0, negative: 0 });
  const [sentimentLoading, setSentimentLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // 5:緑, 4:緑系, 3:黄, 2:オレンジ, 1:赤
  const COLORS = ['#10B981', '#34D399', '#FBBF24', '#F59E0B', '#EF4444'];
  const RADIAN = Math.PI / 180;

  useEffect(() => {
    // console.log('useEffect triggered. shopname:', shopname);
    if (!shopname) {
      // console.log('shopname is empty or undefined. fetchRatings will NOT be called.');
      return;
    }
    const fetchRatings = async () => {
      // console.log('fetchRatings called with shopname:', shopname);
      setLoading(true);
      let query = supabaseClient
        .from('reviews')
        .select('starrating, reviewdate_data', { count: 'exact' })
        .eq('shopname', shopname);
      if (dateRange) {
        const now = new Date();
        const daysAgo = Math.floor((now.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
        query = query.lte('reviewdate_data', daysAgo);
      }
      const { data, error } = await query;
      // console.log('shopname:', shopname);
      // console.log('data:', data);
      // console.log('error:', error);
      if (!error && data) {
        // 1〜5の星ごとにカウント（1,2,3,4,5の順）
        const counts = [1,2,3,4,5].map(star => ({
          rating: star,
          count: data.filter((d: any) => Number(d.starrating) === star).length
        }));
        setRatingDistribution(counts);
      }
      setLoading(false);
    };
    fetchRatings();
  }, [shopname, dateRange]);

  // useEffectの前にfetchSentimentDataを定義
  const fetchSentimentData = async () => {
    if (!shopname || !periodLabel) return;
    setSentimentLoading(true);
    try {
      const { data, error } = await supabaseClient
        .from('keyword_analysis_cache')
        .select('keywords_json')
        .eq('shopname', shopname)
        .eq('period_label', periodLabel)
        .order('created_at', { ascending: false })
        .limit(1);
      if (data && data.length > 0 && data[0].keywords_json) {
        // 感情ごとに集計
        const counts = { positive: 0, neutral: 0, negative: 0 };
        data[0].keywords_json.forEach((item: any) => {
          if (item.sentiment === 'positive') counts.positive += 1;
          else if (item.sentiment === 'neutral') counts.neutral += 1;
          else if (item.sentiment === 'negative') counts.negative += 1;
        });
        setSentimentData(counts);
      } else {
        setSentimentData({ positive: 0, neutral: 0, negative: 0 });
      }
    } catch (e) {
      setSentimentData({ positive: 0, neutral: 0, negative: 0 });
    }
    setSentimentLoading(false);
  };
  useEffect(() => {
    fetchSentimentData();
  }, [shopname, periodLabel]);

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    // 小さなセクション（10%以下）は外側に表示
    const isSmallSection = percent <= 0.10;
    
    if (isSmallSection) {
      // 画面幅に応じて外側ラベルの距離を調整
      const labelDistance = window.innerWidth < 768 ? 20 : 30;
      const outerRadius2 = outerRadius + labelDistance;
      const x1 = cx + outerRadius * Math.cos(-midAngle * RADIAN);
      const y1 = cy + outerRadius * Math.sin(-midAngle * RADIAN);
      const x2 = cx + outerRadius2 * Math.cos(-midAngle * RADIAN);
      const y2 = cy + outerRadius2 * Math.sin(-midAngle * RADIAN);
      
      return (
        <g>
          <line
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="#666"
            strokeWidth={1}
          />
          <text
            x={x2}
            y={y2}
            fill="#666"
            textAnchor={x2 > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontWeight="bold"
            fontSize={window.innerWidth < 768 ? 12 : 14}
          >
            {`${(percent * 100).toFixed(0)}%`}
          </text>
        </g>
      );
    }

    // 大きなセクションは内側に表示
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={window.innerWidth < 768 ? 12 : 14}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const sentimentColors = {
    Positive: '#10B981',
    Neutral: '#6B7280',
    Negative: '#EF4444'
  };

  const getPeriodLabel = (dateRange: { start: Date; end: Date }) => {
    const now = new Date();
    const start = dateRange.start;
    const end = dateRange.end;
    // 今月
    if (
      start.getFullYear() === now.getFullYear() &&
      start.getMonth() === now.getMonth() &&
      start.getDate() === 1 &&
      end.getFullYear() === now.getFullYear() &&
      end.getMonth() === now.getMonth()
    ) return '今月';
    // 先月
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    if (
      start.getFullYear() === lastMonth.getFullYear() &&
      start.getMonth() === lastMonth.getMonth() &&
      start.getDate() === 1 &&
      end.getFullYear() === lastMonth.getFullYear() &&
      end.getMonth() === lastMonth.getMonth()
    ) return '先月';
    // 過去3ヶ月
    const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
    if (
      start.getFullYear() === threeMonthsAgo.getFullYear() &&
      start.getMonth() === threeMonthsAgo.getMonth() &&
      end.getFullYear() === now.getFullYear() &&
      end.getMonth() === now.getMonth()
    ) return '過去3ヶ月';
    // 過去6ヶ月
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
    if (
      start.getFullYear() === sixMonthsAgo.getFullYear() &&
      start.getMonth() === sixMonthsAgo.getMonth() &&
      end.getFullYear() === now.getFullYear() &&
      end.getMonth() === now.getMonth()
    ) return '過去6ヶ月';
    // 過去1年
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
    if (
      start.getFullYear() === oneYearAgo.getFullYear() &&
      start.getMonth() === oneYearAgo.getMonth() &&
      end.getFullYear() === now.getFullYear() &&
      end.getMonth() === now.getMonth()
    ) return '過去1年';
    // 全期間
    if (
      start.getFullYear() === 2000 && start.getMonth() === 0 && start.getDate() === 1
    ) return '全期間';
    return '';
  };

  if (isLoading || loading) {
    return <LoadingSkeleton className="h-[500px]" />;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="border-b border-gray-200 flex items-center justify-between">
        <nav className="flex">
          {[
            { id: 'distribution', label: '評価分布' },
            { id: 'keywords', label: 'キーワード分析' },
            { id: 'sentiment', label: '感情分析' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        {activeTab === 'keywords' && (
          <div className="px-4">
            <button
              onClick={async () => {
                setIsUpdating(true);
                try {
                  if (keywordCloudRef.current?.fetchKeywordData) {
                    await keywordCloudRef.current.fetchKeywordData();
                  }
                  await fetchSentimentData();
                } finally {
                  setIsUpdating(false);
                }
              }}
              disabled={isUpdating}
              className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-md ${
                isUpdating
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
              {isUpdating ? '更新中...' : '更新'}
            </button>
          </div>
        )}
      </div>

      <div className="p-4 md:p-6">
        {activeTab === 'distribution' && (
          <div className="h-[400px] sm:h-[450px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[...ratingDistribution].reverse()}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={window.innerWidth < 768 ? 120 : 150}
                  fill="#8884d8"
                  dataKey="count"
                  startAngle={90}
                  endAngle={-270}
                >
                  {[...ratingDistribution].reverse().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value: number, name: string, props: any) => [`${value} reviews`, `${props.payload.rating} stars`]}
                  contentStyle={{ backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e2e8f0', padding: '8px 12px' }}
                />
                <Legend 
                  formatter={(value: string, entry: any, index: number) => {
                    const reversed = [...ratingDistribution].reverse();
                    return `${reversed[index].rating} Stars (${reversed[index].count})`;
                  }}
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{
                    fontSize: window.innerWidth < 768 ? '12px' : '14px',
                    paddingTop: '10px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}

        {activeTab === 'keywords' && (
          <div className="h-[400px] flex items-center justify-center">
            <KeywordCloud ref={keywordCloudRef} dateRange={dateRange} shopName={shopname} periodLabel={periodLabel} />
          </div>
        )}

        {activeTab === 'sentiment' && (
          <div className="h-[400px] flex flex-col items-center justify-center">
            {sentimentLoading ? (
              <div>読み込み中...</div>
            ) : (
              (sentimentData.positive === 0 && sentimentData.neutral === 0 && sentimentData.negative === 0) ? (
                <div className="text-gray-500">データが見つかりません。キーワード分析の更新ボタンを押してください。</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={[
                      { name: 'ポジティブ', value: sentimentData.positive, fill: '#10B981' },
                      { name: 'ネガティブ', value: sentimentData.negative, fill: '#EF4444' },
                      { name: 'ニュートラル', value: sentimentData.neutral, fill: '#6B7280' },
                    ]}
                    layout="vertical"
                    margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="value" name="件数" isAnimationActive={false} >
                      <Cell key="positive" fill="#10B981" />
                      <Cell key="negative" fill="#EF4444" />
                      <Cell key="neutral" fill="#6B7280" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedAnalysis;